import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentFrequency } from '@if/core/types'
import { dateOnly, InferSubmit, mixed, object, ref } from '@if/core/yup'
import { useIfUser } from '@if/mui'
import { SUPPORTED_FREQUENCIES } from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { previewedSimulation } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-schema'
import { isNewFirstPaymentValidForIntervals } from './LoanCalendarFrequency-selectors'

/**
 * The simulation is closed over rather than reached through a `$`-ref: the dialog builds its
 * schema from the calendar it opens on, so the rules read it directly and the form needs no
 * validation context.
 */
export const getNewFrequencySchema = (simulation: CalendarSimulation, canBypassAcpLock: boolean) =>
  object({
    frequency: mixed<EPaymentFrequency>()
      .oneOf(SUPPORTED_FREQUENCIES)
      .required()
      .test(
        'test-sameFrequency',
        () => t('loan.payment.editPaymentFrequency.errors.sameFrequency'),
        (value, context) => {
          const firstPaymentDate = context.resolve<DateOnly | null>(ref('firstPaymentDate'))
          if (!firstPaymentDate) return true // If first payment date is not set, we cannot validate frequency

          const currentPaymentConfig = simulation.getRepaymentConfigAtDate(firstPaymentDate)

          // Get the last payment config
          const lastPaymentConfig = simulation.fullLoan.paymentConfigs[simulation.fullLoan.paymentConfigs.length - 1]

          // Only validate if the current config is the last one AND frequency is the same
          if (currentPaymentConfig?.id === lastPaymentConfig?.id && value === currentPaymentConfig.frequency) {
            return false
          }

          return true
        },
      ),
    firstPaymentDate: dateOnly()
      .default(null)
      .required()
      .test(
        'test-inIntervals',
        () => t('loan.payment.editPaymentFrequency.errors.firstPaymentDateInvalid'),
        (value, context) =>
          isNewFirstPaymentValidForIntervals(
            simulation,
            context.resolve<EPaymentFrequency>(ref('frequency')),
            value,
            canBypassAcpLock,
          ),
      ),
    previewedSimulation: previewedSimulation(),
  })

export type NewFrequencySchemaType = InferSubmit<ReturnType<typeof getNewFrequencySchema>>

export const useNewFrequencySchema = (simulation: CalendarSimulation) => {
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  return useMemo(() => getNewFrequencySchema(simulation, canBypassAcpLock), [simulation, canBypassAcpLock])
}
